/**
 * D20Dice — Cinematic d20 dice roller
 *
 * A faceted icosahedron silhouette with gold gradients, SVG glow filters,
 * and full-screen modal presentation.
 *
 * Exports:
 *   - D20Dice: the modal roller (controlled via isOpen/onClose)
 *   - D20DiceButton: a small header trigger that manages its own open state
 */

import { useState, useEffect, useCallback, useRef, useId, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// ============================================================================
// Timing constants
// ============================================================================

const ROLL_DURATION_MS = 1900;           // Total spin time
const ROLL_DURATION_REDUCED_MS = 500;    // Shorter spin when user prefers reduced motion
const NUMBER_CYCLE_MS = 70;              // How fast numbers flicker during the roll
const NUMBER_CYCLE_REDUCED_MS = 120;     // Slower cycle in reduced-motion mode
const AUTO_ROLL_DELAY_MS = 320;          // Small delay after modal opens before auto-rolling

// ============================================================================
// User personalisation + reduced-motion helpers
// ============================================================================

function getUserFirstName(): string {
  if (typeof window === 'undefined') return '';
  const raw = localStorage.getItem('logged_in_user') || '';
  const first = raw.trim().split(/\s+/)[0] || '';
  // Capitalise in case the stored value is lowercase.
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : '';
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ============================================================================
// Particle types
// ============================================================================

interface Particle {
  id: string;
  angle: number;      // radians
  distance: number;   // px from center
  rotation: number;   // deg total rotation
  delay: number;      // seconds before start
  duration: number;   // seconds
  size: number;       // px
  drift: number;      // px of lateral drift during flight
}

function makeParticles(count: number): Particle[] {
  return Array.from({ length: count }, () => ({
    // Unique ID prevents React from reusing DOM nodes on back-to-back crits,
    // which would otherwise skip the enter animation.
    id: `${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`,
    angle: Math.random() * Math.PI * 2,
    distance: 110 + Math.random() * 130,
    rotation: (Math.random() - 0.5) * 720,
    delay: Math.random() * 0.1,
    duration: 0.95 + Math.random() * 0.55,
    size: 4 + Math.random() * 7,
    drift: (Math.random() - 0.5) * 30,
  }));
}

// ============================================================================
// Geometry — regular pentagon vertices for a faceted D20 silhouette
// ============================================================================

// Outer silhouette pentagon, r=45, point-up, centered at (50, 50)
const OUTER = [
  { x: 50.0, y: 5.0 },    // 0 — top
  { x: 92.8, y: 36.09 },  // 1 — upper-right
  { x: 76.45, y: 86.41 }, // 2 — lower-right
  { x: 23.55, y: 86.41 }, // 3 — lower-left
  { x: 7.2, y: 36.09 },   // 4 — upper-left
];

// Inner "top face" pentagon, r=20, point-DOWN (rotated 36°) — this is the
// visible triangular-face cluster of the icosahedron from this angle.
const INNER = [
  { x: 61.76, y: 33.82 }, // 0 — upper-right
  { x: 69.02, y: 56.18 }, // 1 — right
  { x: 50.0,  y: 70.0 },  // 2 — bottom
  { x: 30.98, y: 56.18 }, // 3 — left
  { x: 38.24, y: 33.82 }, // 4 — upper-left
];

const pt = (p: { x: number; y: number }) => `${p.x},${p.y}`;

// Five "bevel" triangles at each outer vertex (inward-pointing), darker shade
const BEVEL_TRIS: string[] = [
  `${pt(OUTER[0])} ${pt(INNER[4])} ${pt(INNER[0])}`, // top bevel
  `${pt(OUTER[1])} ${pt(INNER[0])} ${pt(INNER[1])}`, // upper-right bevel
  `${pt(OUTER[2])} ${pt(INNER[1])} ${pt(INNER[2])}`, // lower-right bevel
  `${pt(OUTER[3])} ${pt(INNER[2])} ${pt(INNER[3])}`, // lower-left bevel
  `${pt(OUTER[4])} ${pt(INNER[3])} ${pt(INNER[4])}`, // upper-left bevel
];

// Five "edge" triangles at each outer edge (outward-pointing), medium shade
const EDGE_TRIS: string[] = [
  `${pt(OUTER[0])} ${pt(OUTER[1])} ${pt(INNER[0])}`, // top-right
  `${pt(OUTER[1])} ${pt(OUTER[2])} ${pt(INNER[1])}`, // right
  `${pt(OUTER[2])} ${pt(OUTER[3])} ${pt(INNER[2])}`, // bottom
  `${pt(OUTER[3])} ${pt(OUTER[4])} ${pt(INNER[3])}`, // left
  `${pt(OUTER[4])} ${pt(OUTER[0])} ${pt(INNER[4])}`, // top-left
];

// Inner pentagon (top face where the number sits)
const INNER_PENTAGON = INNER.map(pt).join(' ');

// ============================================================================
// D20 SVG — the dice itself
// ============================================================================

interface D20SVGProps {
  value: number;
  size?: number;
  tone?: 'gold' | 'crit' | 'fail';
}

function D20SVG({ value, size = 260, tone = 'gold' }: D20SVGProps) {
  // Unique per-instance IDs so multiple D20s on one page never clash.
  const uid = useId().replace(/:/g, '');
  const ids = {
    body: `d20-body-${uid}`,
    bevel: `d20-bevel-${uid}`,
    edge: `d20-edge-${uid}`,
    face: `d20-face-${uid}`,
    glow: `d20-glow-${uid}`,
    spec: `d20-spec-${uid}`,
  };

  // Color palettes per tone — gold by default, bright gold on crit, crimson on fail
  const palette = {
    gold: {
      top: '#f4d06f',
      mid: '#c5a065',
      dark: '#7a5a2c',
      edge: '#9a7a3f',
      bevel: '#5c4420',
      face: '#ffe9a8',
      stroke: '#3d2e15',
      number: '#1a2238',
      glow: '#ffd97a',
    },
    crit: {
      top: '#ffed8a',
      mid: '#ffcf45',
      dark: '#a87a1e',
      edge: '#c49430',
      bevel: '#7a5716',
      face: '#fff8d0',
      stroke: '#4a330a',
      number: '#1a2238',
      glow: '#ffe066',
    },
    fail: {
      top: '#ff8a7a',
      mid: '#c24b3a',
      dark: '#6b1f14',
      edge: '#8c2f1f',
      bevel: '#4a1208',
      face: '#ffcdbf',
      stroke: '#2d0705',
      number: '#1a0505',
      glow: '#ff6347',
    },
  }[tone];

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        {/* Main body gradient — radial, bright upper-left to dark lower-right */}
        <radialGradient id={ids.body} cx="35%" cy="25%" r="85%">
          <stop offset="0%" stopColor={palette.top} />
          <stop offset="45%" stopColor={palette.mid} />
          <stop offset="100%" stopColor={palette.dark} />
        </radialGradient>

        {/* Bevel gradient — darker, angular facets */}
        <linearGradient id={ids.bevel} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={palette.bevel} />
          <stop offset="100%" stopColor={palette.dark} />
        </linearGradient>

        {/* Edge gradient — medium sidewall */}
        <linearGradient id={ids.edge} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={palette.edge} />
          <stop offset="100%" stopColor={palette.bevel} />
        </linearGradient>

        {/* Top face — shiny, bright */}
        <radialGradient id={ids.face} cx="40%" cy="35%" r="75%">
          <stop offset="0%" stopColor={palette.face} />
          <stop offset="60%" stopColor={palette.top} />
          <stop offset="100%" stopColor={palette.mid} />
        </radialGradient>

        {/* Outer glow filter */}
        <filter id={ids.glow} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Specular highlight — small bright spot on upper-left */}
        <radialGradient id={ids.spec} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="70%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Drop shadow circle beneath — adds depth */}
      <ellipse cx="50" cy="94" rx="28" ry="3" fill="#000" opacity="0.35" />

      <g filter={`url(#${ids.glow})`}>
        {/* Outer silhouette fill — base body */}
        <polygon
          points={OUTER.map(pt).join(' ')}
          fill={`url(#${ids.body})`}
        />

        {/* 5 edge triangles (outward-pointing, at each outer edge) */}
        {EDGE_TRIS.map((p, i) => (
          <polygon key={`edge-${i}`} points={p} fill={`url(#${ids.edge})`} />
        ))}

        {/* 5 bevel triangles (inward-pointing, at each outer vertex) */}
        {BEVEL_TRIS.map((p, i) => (
          <polygon key={`bevel-${i}`} points={p} fill={`url(#${ids.bevel})`} />
        ))}

        {/* Top face pentagon — shiny */}
        <polygon points={INNER_PENTAGON} fill={`url(#${ids.face})`} />

        {/* Inner triangulation (5 lines from center to inner vertices) */}
        <g stroke={palette.bevel} strokeWidth="0.35" opacity="0.55">
          {INNER.map((v, i) => (
            <line key={`tri-${i}`} x1="50" y1="50" x2={v.x} y2={v.y} />
          ))}
        </g>

        {/* Outer edges stroke — defines silhouette */}
        <polygon
          points={OUTER.map(pt).join(' ')}
          fill="none"
          stroke={palette.stroke}
          strokeWidth="0.6"
          strokeLinejoin="round"
        />

        {/* Inner pentagon stroke */}
        <polygon
          points={INNER_PENTAGON}
          fill="none"
          stroke={palette.bevel}
          strokeWidth="0.4"
          strokeLinejoin="round"
          opacity="0.7"
        />

        {/* Specular highlight — upper-left corner */}
        <ellipse
          cx="32"
          cy="28"
          rx="14"
          ry="9"
          fill={`url(#${ids.spec})`}
          transform="rotate(-25 32 28)"
        />

        {/* The number */}
        <text
          x="50"
          y="52"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={value >= 10 ? 18 : 22}
          fontWeight="900"
          fill={palette.number}
          fontFamily="Georgia, 'Times New Roman', serif"
          style={{
            letterSpacing: '-0.5px',
            textShadow: `0 0 2px ${palette.glow}`,
          }}
        >
          {value}
        </text>
      </g>
    </svg>
  );
}

// ============================================================================
// D20Dice — the modal roller
// ============================================================================

interface D20DiceProps {
  isOpen: boolean;
  onClose: () => void;
}

type RollStatus = 'idle' | 'rolling' | 'settled';

export function D20Dice({ isOpen, onClose }: D20DiceProps) {
  // The number currently shown on the dice face (cycles rapidly while rolling).
  const [displayValue, setDisplayValue] = useState(20);
  // The actual locked-in result (set at roll start, revealed at settle).
  const [settledValue, setSettledValue] = useState(20);
  const [status, setStatus] = useState<RollStatus>('idle');
  // Particle batches for celebration — regenerated every roll.
  const [particles, setParticles] = useState<Particle[]>([]);
  // Last 5 results, newest first.
  const [history, setHistory] = useState<number[]>([]);

  // Refs to manage the in-flight roll (avoids stale-closure issues in timers).
  const rollingRef = useRef(false);
  const cycleIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lockIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rollButtonRef = useRef<HTMLButtonElement>(null);

  // Read once per mount — personal label + reduced-motion preference.
  const firstName = useMemo(() => getUserFirstName(), []);
  const labelText = firstName ? `${firstName}'s Lucky d20` : 'Lucky d20';
  const reducedMotion = useMemo(() => prefersReducedMotion(), []);
  const rollDuration = reducedMotion ? ROLL_DURATION_REDUCED_MS : ROLL_DURATION_MS;
  const cycleInterval = reducedMotion ? NUMBER_CYCLE_REDUCED_MS : NUMBER_CYCLE_MS;

  const clearTimers = useCallback(() => {
    if (cycleIdRef.current) {
      clearInterval(cycleIdRef.current);
      cycleIdRef.current = null;
    }
    if (lockIdRef.current) {
      clearTimeout(lockIdRef.current);
      lockIdRef.current = null;
    }
  }, []);

  const roll = useCallback(() => {
    if (rollingRef.current) return;
    rollingRef.current = true;

    const final = Math.floor(Math.random() * 20) + 1;
    setStatus('rolling');

    // Kick-off haptic — a single short buzz when the dice leave the hand.
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      try { navigator.vibrate(20); } catch { /* noop */ }
    }

    // Rapid number cycling — makes it feel like a real physics sim.
    cycleIdRef.current = setInterval(() => {
      setDisplayValue(Math.floor(Math.random() * 20) + 1);
    }, cycleInterval);

    // Lock in the result when the spin settles.
    lockIdRef.current = setTimeout(() => {
      if (cycleIdRef.current) {
        clearInterval(cycleIdRef.current);
        cycleIdRef.current = null;
      }
      setDisplayValue(final);
      setSettledValue(final);
      setStatus('settled');

      // Fresh particle batch — count scales with drama.
      const count = final === 20 ? 46 : final === 1 ? 30 : 10;
      setParticles(makeParticles(count));

      // Log to history (newest first, cap at 5).
      setHistory((prev) => [final, ...prev].slice(0, 5));

      // Settle haptic — tiered by outcome so crits really land.
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        try {
          if (final === 20) navigator.vibrate([0, 80, 60, 80, 60, 140]);
          else if (final === 1) navigator.vibrate([0, 45, 40, 45]);
          else navigator.vibrate(55);
        } catch { /* noop */ }
      }

      rollingRef.current = false;
    }, rollDuration);
  }, [rollDuration, cycleInterval]);

  // Keyboard: Esc closes, Space/Enter rolls again. Both blocked mid-roll.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (rollingRef.current) return;
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === ' ' || e.key === 'Enter') {
        // Don't hijack typing in inputs (defensive — no inputs in modal today).
        const tag = (e.target as HTMLElement | null)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        e.preventDefault();
        roll();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose, roll]);

  // Auto-roll on open + full reset on close.
  useEffect(() => {
    if (!isOpen) {
      clearTimers();
      rollingRef.current = false;
      setStatus('idle');
      setParticles([]);
      setHistory([]);
      return;
    }
    const t = setTimeout(roll, AUTO_ROLL_DELAY_MS);
    return () => clearTimeout(t);
  }, [isOpen, roll, clearTimers]);

  // Safety cleanup on unmount.
  useEffect(() => () => clearTimers(), [clearTimers]);

  // Lock body scroll while the modal is open (prevents iOS bleed-through).
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  // Move focus into the modal after entry animation so keyboard users get
  // context + can Enter/Space to re-roll immediately.
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => rollButtonRef.current?.focus(), 450);
    return () => clearTimeout(t);
  }, [isOpen]);

  const tone: 'gold' | 'crit' | 'fail' =
    status === 'settled'
      ? settledValue === 20
        ? 'crit'
        : settledValue === 1
        ? 'fail'
        : 'gold'
      : 'gold';

  const isRolling = status === 'rolling';

  // Block backdrop-close while mid-roll so user doesn't accidentally kill it.
  const handleBackdropClick = () => {
    if (!isRolling) onClose();
  };

  const rollButtonLabel =
    status === 'idle' ? 'Roll the d20' : isRolling ? 'Rolling…' : 'Roll Again';

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="d20-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="d20-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={handleBackdropClick}
          className="fixed inset-0 z-[300] flex items-center justify-center p-4"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(15,21,33,0.85) 0%, rgba(8,12,20,0.95) 70%, rgba(0,0,0,0.98) 100%)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
          }}
        >
          {/* Screen flash — radiates outward from dice on Nat 20 */}
          <AnimatePresence>
            {status === 'settled' && settledValue === 20 && (
              <motion.div
                key="d20-flash"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.45, 0] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, times: [0, 0.18, 1], ease: 'easeOut' }}
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(circle at center, rgba(255,215,80,0.7) 0%, rgba(255,180,60,0.25) 35%, rgba(197,160,101,0) 70%)',
                }}
              />
            )}
          </AnimatePresence>

          <motion.div
            key="d20-stage"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 280, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="relative flex flex-col items-center gap-6"
            style={{ perspective: '1200px' }}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute -top-2 -right-2 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white flex items-center justify-center transition-colors backdrop-blur-sm z-10"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Outcome banner — Nat 20 shimmer / Nat 1 doom */}
            <div className="h-16 flex items-end justify-center pointer-events-none">
              <AnimatePresence mode="wait">
                {status === 'settled' && settledValue === 20 && (
                  <motion.div
                    key="banner-crit"
                    initial={{ opacity: 0, y: 20, scale: 0.6 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.9 }}
                    transition={{ type: 'spring', stiffness: 280, damping: 16, delay: 0.15 }}
                    className="flex flex-col items-center"
                  >
                    <div className="text-[10px] text-brand-secondary/80 tracking-[0.45em] font-semibold uppercase mb-1">
                      Natural Twenty
                    </div>
                    <motion.div
                      animate={
                        reducedMotion
                          ? { backgroundPosition: '0% 50%' }
                          : { backgroundPosition: ['0% 50%', '200% 50%'] }
                      }
                      transition={
                        reducedMotion
                          ? { duration: 0 }
                          : { duration: 2.6, repeat: Infinity, ease: 'linear' }
                      }
                      className="text-3xl md:text-4xl font-black tracking-wider"
                      style={{
                        backgroundImage:
                          'linear-gradient(90deg, #fff2b0 0%, #ffd97a 20%, #ffe066 40%, #fff8d0 50%, #ffe066 60%, #ffd97a 80%, #fff2b0 100%)',
                        backgroundSize: '200% 100%',
                        WebkitBackgroundClip: 'text',
                        backgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        filter: 'drop-shadow(0 0 14px rgba(255,215,80,0.55))',
                        fontFamily: "Georgia, 'Times New Roman', serif",
                      }}
                    >
                      CRITICAL HIT
                    </motion.div>
                  </motion.div>
                )}

                {status === 'settled' && settledValue === 1 && (
                  <motion.div
                    key="banner-fail"
                    initial={{ opacity: 0, y: 20, scale: 0.6 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      scale: 1,
                      x: reducedMotion ? 0 : [0, -3, 3, -2, 2, 0],
                    }}
                    exit={{ opacity: 0, y: -10, scale: 0.9 }}
                    transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 18,
                      delay: 0.15,
                      x: { duration: 0.4, delay: 0.3 },
                    }}
                    className="flex flex-col items-center"
                  >
                    <div className="text-[10px] text-red-400/80 tracking-[0.45em] font-semibold uppercase mb-1">
                      Natural One
                    </div>
                    <div
                      className="text-3xl md:text-4xl font-black tracking-wider text-red-500"
                      style={{
                        textShadow:
                          '0 0 18px rgba(239,68,68,0.65), 0 0 6px rgba(100,20,20,0.9)',
                        fontFamily: "Georgia, 'Times New Roman', serif",
                      }}
                    >
                      CRITICAL FAIL
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Dice stage — 3D tumbling container */}
            <div
              className="relative flex items-center justify-center"
              style={{ width: 280, height: 280, perspective: '1000px' }}
            >
              {/* Particle burst — radiates from center on settle */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                {particles.map((p) => {
                  const palette =
                    settledValue === 1
                      ? {
                          bg: 'radial-gradient(circle, #ffb095 0%, #ef4444 45%, rgba(136,20,20,0) 100%)',
                          shadow: '0 0 10px rgba(239,68,68,0.7), 0 0 3px rgba(255,120,90,0.9)',
                        }
                      : {
                          bg: 'radial-gradient(circle, #fff8d0 0%, #ffd97a 45%, rgba(197,160,101,0) 100%)',
                          shadow: '0 0 10px rgba(255,215,80,0.75), 0 0 3px rgba(255,240,170,0.9)',
                        };
                  const xEnd = Math.cos(p.angle) * p.distance + p.drift;
                  const yEnd = Math.sin(p.angle) * p.distance;
                  return (
                    <motion.div
                      key={p.id}
                      initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                      animate={{
                        x: xEnd,
                        y: yEnd,
                        opacity: [0, 1, 1, 0],
                        scale: [0, 1.2, 1, 0.6],
                        rotate: p.rotation,
                      }}
                      transition={{
                        duration: p.duration,
                        delay: p.delay,
                        ease: 'easeOut',
                        opacity: { times: [0, 0.12, 0.6, 1], duration: p.duration, delay: p.delay },
                        scale: { times: [0, 0.2, 0.6, 1], duration: p.duration, delay: p.delay },
                      }}
                      className="absolute rounded-full"
                      style={{
                        width: p.size,
                        height: p.size,
                        background: palette.bg,
                        boxShadow: palette.shadow,
                      }}
                    />
                  );
                })}
              </div>

              {/* Soft ambient glow under dice (breathes with status) */}
              <motion.div
                aria-hidden
                className="absolute inset-0 rounded-full"
                animate={{
                  opacity: isRolling && !reducedMotion ? [0.35, 0.55, 0.35] : 0.4,
                  scale: isRolling && !reducedMotion ? [0.85, 1.1, 0.85] : 1,
                }}
                transition={{
                  duration: isRolling ? 0.9 : 0.6,
                  repeat: isRolling && !reducedMotion ? Infinity : 0,
                  ease: 'easeInOut',
                }}
                style={{
                  background:
                    'radial-gradient(circle, rgba(197,160,101,0.35) 0%, rgba(197,160,101,0) 60%)',
                  filter: 'blur(20px)',
                }}
              />

              {/* The dice itself — tumbles on all three axes while rolling.
                  Reduced-motion users get a gentle scale pulse instead of a
                  full 3D tumble to avoid motion sickness. */}
              <motion.div
                style={{ transformStyle: 'preserve-3d', willChange: 'transform' }}
                animate={
                  isRolling
                    ? reducedMotion
                      ? { scale: [1, 1.05, 1] }
                      : {
                          rotateX: [0, 540, 1080],
                          rotateY: [0, 720, 900],
                          rotateZ: [0, 270, 540],
                          scale: [1, 1.18, 1],
                        }
                    : reducedMotion
                    ? { rotateX: 0, rotateY: 0, rotateZ: 0, scale: 1 }
                    : {
                        rotateX: 0,
                        rotateY: 0,
                        rotateZ: 0,
                        scale: status === 'settled' ? [1.15, 0.95, 1.05, 1] : 1,
                      }
                }
                transition={
                  isRolling
                    ? {
                        duration: rollDuration / 1000,
                        times: reducedMotion ? [0, 0.5, 1] : [0, 0.55, 1],
                        ease: reducedMotion ? 'easeOut' : [0.18, 0.72, 0.28, 1],
                      }
                    : status === 'settled' && !reducedMotion
                    ? {
                        duration: 0.55,
                        times: [0, 0.35, 0.7, 1],
                        ease: 'easeOut',
                      }
                    : { type: 'spring', stiffness: 260, damping: 22 }
                }
              >
                <D20SVG value={displayValue} size={260} tone={tone} />
              </motion.div>
            </div>

            {/* Roll button */}
            <button
              ref={rollButtonRef}
              onClick={roll}
              disabled={isRolling}
              className="px-8 py-3 rounded-full bg-gradient-to-r from-brand-secondary to-brand-secondary/70 text-brand-primary font-bold tracking-wide shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{
                boxShadow:
                  '0 8px 30px rgba(197,160,101,0.45), 0 0 40px rgba(197,160,101,0.25)',
              }}
            >
              {rollButtonLabel}
            </button>

            {/* Recent rolls */}
            <div className="h-9 flex items-center justify-center gap-1.5 min-w-[220px]">
              <AnimatePresence mode="popLayout">
                {history.length > 0 && (
                  <motion.span
                    key="history-label"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-[10px] text-white/35 tracking-[0.25em] uppercase mr-1 font-medium"
                  >
                    Recent
                  </motion.span>
                )}
                {history.map((v, i) => (
                  <motion.div
                    key={`hist-${i}-${v}`}
                    layout
                    initial={{ opacity: 0, scale: 0.4, y: -8 }}
                    animate={{ opacity: 1 - i * 0.14, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.6 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black font-serif border ${
                      v === 20
                        ? 'bg-brand-secondary/20 text-brand-secondary border-brand-secondary/40'
                        : v === 1
                        ? 'bg-red-500/15 text-red-400 border-red-500/35'
                        : 'bg-white/5 text-white/65 border-white/10'
                    }`}
                  >
                    {v}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Label + subtle keyboard hint */}
            <div className="flex flex-col items-center gap-1">
              <div id="d20-title" className="text-white/50 text-xs font-medium tracking-[0.2em] uppercase">
                {labelText}
              </div>
              <div className="text-white/25 text-[9px] tracking-[0.15em] uppercase">
                Space to roll &middot; Esc to close
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

// ============================================================================
// D20DiceButton — small header trigger
// ============================================================================

interface D20DiceButtonProps {
  /** Visual variant to match surrounding header style.
   *  - 'office' (default): theme-aware, used on desktop office header
   *  - 'tech': white-on-navy, used in the tech mobile header */
  variant?: 'office' | 'tech';
}

export function D20DiceButton({ variant = 'office' }: D20DiceButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const buttonClass =
    variant === 'tech'
      ? 'w-8 h-8 flex items-center justify-center text-white/30 hover:text-brand-secondary hover:bg-white/10 rounded-xl transition-all active:scale-95'
      : 'p-1.5 rounded-lg text-theme-tertiary hover:text-brand-secondary hover:bg-surface-hover transition-all';

  const iconClass = variant === 'tech' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`group ${buttonClass}`}
        title="Roll a d20"
        aria-label="Roll a d20"
      >
        {/* Mini d20 icon — stylized pentagonal silhouette.
            Rotates slightly on hover to hint at its rolling nature. */}
        <svg
          viewBox="0 0 24 24"
          className={`${iconClass} transition-transform duration-300 ease-out group-hover:rotate-[18deg] group-active:rotate-[-8deg]`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        >
          <polygon points="12,2 22,9 18,21 6,21 2,9" />
          <polygon points="14.9,8.1 16.7,13.5 12,17 7.3,13.5 9.1,8.1" opacity="0.6" />
          <text x="12" y="14.5" textAnchor="middle" fontSize="6" fontWeight="900" fill="currentColor" stroke="none" fontFamily="Georgia, serif">
            20
          </text>
        </svg>
      </button>
      <D20Dice isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
}
